import {
    LayoutDashboard, Building2, Users, GitPullRequest, ClipboardList,
    FileText, Sparkles, Mail, CheckSquare, BarChart3, Settings,
    Package, ListChecks, Inbox, CalendarDays, HeartPulse, Lightbulb,
    Workflow, Rocket, MessageSquareQuote, BookOpen,
    Bot, ShieldCheck, Shield, Layers,
    ShoppingCart, Megaphone, Headphones, Cog, Monitor, UserCheck, Scale, DollarSign,
    CalendarCheck, KanbanSquare
} from 'lucide-react';

// ─── Universal items (shown in every pillar's sidebar) ─────────────────────

export const UNIVERSAL_TOP = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { label: 'Inboxes', icon: Inbox, path: '/inbox' },
    { label: 'Tasks', icon: CheckSquare, path: '/tasks', badge: 'tasks' },
];

// UNIVERSAL_AI was deleted 2026-09-02. The AI pages are reached from
// `config/workspaces.js`, which is what actually drives the sidebar; this was a
// second, parallel definition that only `utils/pageId.js` still read.
//
// Deleting the const alone would have been a module-eval ReferenceError, because
// `UNIVERSAL_GROUPS` below referenced it — and `pillarDefs` is imported by
// `pageId.js`, which `RuntimeShell` calls on EVERY render. That failure is a
// white screen on every page, not a broken AI page. `UNIVERSAL_GROUPS` had zero
// consumers repo-wide, so it went too rather than being repaired.

export const UNIVERSAL_SUPPORT = {
    id: 'support',
    label: 'Support',
    icon: Lightbulb,
    items: [
        { label: 'Help Guide', icon: BookOpen, path: '/help' },
        { label: 'Feature Requests', icon: Lightbulb, path: '/feature-requests' },
        { label: 'Release Notes', icon: Rocket, path: '/release-notes' },
    ],
};

export const BOTTOM_PINNED = [
    { label: 'Settings', icon: Settings, path: '/settings/profile' },
    { label: 'Admin', icon: ShieldCheck, path: '/admin/organization', adminOnly: true },
];

// ─── Pillar definitions ────────────────────────────────────────────────────

export const PILLAR_DEFS = [
    {
        id: 'marketing',
        label: 'Marketing',
        icon: Megaphone,
        comingSoon: false,
        moduleKey: null,
        defaultPath: '/campaigns',
        pathPrefixes: ['/campaigns', '/events', '/forms', '/bookings', '/email-sequences', '/testimonials', '/list-management'],
        sections: [
            {
                id: 'marketing-outreach',
                label: 'Outreach',
                icon: Mail,
                items: [
                    { label: 'Campaigns', icon: Workflow, path: '/campaigns', feature: 'campaigns' },
                    { label: 'Events', icon: CalendarDays, path: '/events', feature: 'events' },
                    { label: 'Forms', icon: ClipboardList, path: '/forms', feature: 'forms' },
                    { label: 'Bookings', icon: CalendarCheck, path: '/bookings', feature: 'forms' },
                    { label: 'Testimonials', icon: MessageSquareQuote, path: '/testimonials' },
                ],
            },
            {
                id: 'marketing-insights',
                label: 'Insights',
                icon: BarChart3,
                items: [
                    { label: 'Activities', icon: CheckSquare, path: '/activities' },
                    { label: 'Reports', icon: BarChart3, path: '/reports' },
                    { label: 'List Management', icon: ListChecks, path: '/list-management', adminOnly: true, feature: 'list_management' },
                ],
            },
        ],
    },
    {
        id: 'sales',
        label: 'Sales',
        icon: ShoppingCart,
        comingSoon: false,
        moduleKey: null,
        defaultPath: '/pipeline',
        pathPrefixes: ['/companies', '/contacts', '/pipeline', '/lead-pipeline', '/deals', '/deal-rooms', '/deal-registration', '/products', '/proposals', '/booking-links'],
        sections: [
            {
                id: 'sales-crm',
                label: 'CRM',
                icon: Building2,
                items: [
                    { label: 'Companies', icon: Building2, path: '/companies' },
                    { label: 'Contacts', icon: Users, path: '/contacts' },
                    { label: 'Deal Pipeline', icon: GitPullRequest, path: '/pipeline' },
                    { label: 'Lead Pipeline', icon: KanbanSquare, path: '/lead-pipeline' },
                    { label: 'Deal Rooms', icon: Layers, path: '/deal-rooms', feature: 'deal_rooms' },
                    { label: 'Products', icon: Package, path: '/products' },
                    { label: 'Proposals', icon: FileText, path: '/proposals' },
                    { label: 'Booking Links', icon: CalendarDays, path: '/booking-links', feature: 'booking_links' },
                ],
            },
            {
                id: 'sales-insights',
                label: 'Insights',
                icon: BarChart3,
                items: [
                    { label: 'Activities', icon: CheckSquare, path: '/activities' },
                    { label: 'Reports', icon: BarChart3, path: '/reports' },
                ],
            },
        ],
    },
    {
        id: 'customer-service',
        label: 'Service',
        icon: Headphones,
        comingSoon: true,
        moduleKey: null,
        defaultPath: null,
        pathPrefixes: [],
        sections: [],
        description: 'Manage customer support tickets, knowledge base, live chat, and customer satisfaction tracking.',
    },
    {
        id: 'operations',
        label: 'Operations',
        icon: Cog,
        comingSoon: true,
        moduleKey: null,
        defaultPath: null,
        pathPrefixes: [],
        sections: [],
        description: 'Streamline workflows, project management, inventory, and operational processes.',
    },
    {
        id: 'it',
        label: 'IT',
        icon: Monitor,
        comingSoon: true,
        moduleKey: null,
        defaultPath: null,
        pathPrefixes: [],
        sections: [],
        description: 'IT asset management, helpdesk, infrastructure monitoring, and security compliance.',
    },
    {
        id: 'hr',
        label: 'HR',
        icon: UserCheck,
        comingSoon: true,
        moduleKey: null,
        defaultPath: null,
        pathPrefixes: [],
        sections: [],
        description: 'HR module — manage recruitment, job orders, and talent pools. Available as an external module provider.',
    },
    {
        id: 'legal',
        label: 'Legal',
        icon: Scale,
        comingSoon: true,
        moduleKey: null,
        defaultPath: null,
        pathPrefixes: [],
        sections: [],
        description: 'Contract management, compliance tracking, legal document templates, and approval workflows.',
    },
    {
        id: 'finance',
        label: 'Finance',
        icon: DollarSign,
        comingSoon: true,
        moduleKey: null,
        defaultPath: null,
        pathPrefixes: [],
        sections: [],
        description: 'Invoicing, expense management, financial reporting, and budget tracking.',
    },
];

// Build a flat list of all universal group objects for the sidebar
